import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ReactLenis, useLenis } from "lenis/react";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { ContentProvider, useSite } from "@/context/ContentContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PageWrapper from "@/components/layout/PageWrapper";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import ChatWidget from "@/components/ChatWidget";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import RoomsPage from "@/pages/RoomsPage";
import ServicesPage from "@/pages/ServicesPage";
import GalleryPage from "@/pages/GalleryPage";
import VideosPage from "@/pages/VideosPage";
import ContactPage from "@/pages/ContactPage";
import KonumPage from "@/pages/KonumPage";
import AdminApp from "@/pages/admin/AdminApp";

const ScrollManager = () => {
  const { pathname } = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname, lenis]);
  return null;
};

const PresenceTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    let sid = localStorage.getItem("ada_visitor_sid");
    if (!sid) {
      sid = `v-${Math.random().toString(36).slice(2)}-${Date.now()}`;
      localStorage.setItem("ada_visitor_sid", sid);
    }
    const api = `${process.env.REACT_APP_BACKEND_URL}/api/presence/ping`;
    const ping = () => {
      fetch(api, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, path: pathname }),
        keepalive: true,
      }).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 30000);
    return () => clearInterval(t);
  }, [pathname]);
  return null;
};

const SeoPage = ({ pageKey, children }) => {
  const D = useSite();
  const p = D.pages[pageKey] || {};
  return (
    <PageWrapper title={p.seoTitle} description={p.seoDesc}>
      {children}
    </PageWrapper>
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<SeoPage pageKey="home"><HomePage /></SeoPage>} />
        <Route path="/hakkimizda" element={<SeoPage pageKey="hakkimizda"><AboutPage /></SeoPage>} />
        <Route path="/odalar" element={<SeoPage pageKey="odalar"><RoomsPage /></SeoPage>} />
        <Route path="/hizmetler" element={<SeoPage pageKey="hizmetler"><ServicesPage /></SeoPage>} />
        <Route path="/galeri" element={<SeoPage pageKey="galeri"><GalleryPage /></SeoPage>} />
        <Route path="/videolar" element={<SeoPage pageKey="videolar"><VideosPage /></SeoPage>} />
        <Route path="/konum" element={<SeoPage pageKey="konum"><KonumPage /></SeoPage>} />
        <Route path="/iletisim" element={<SeoPage pageKey="iletisim"><ContactPage /></SeoPage>} />
        <Route path="/admin" element={<AdminApp />} />
        <Route path="*" element={<SeoPage pageKey="home"><HomePage /></SeoPage>} />
      </Routes>
    </AnimatePresence>
  );
};

const Shell = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <div className="relative flex min-h-screen flex-col bg-cream-50">
      {!isAdmin && <Navbar />}
      <AnimatedRoutes />
      {!isAdmin && <Footer />}
      {!isAdmin && <WhatsAppFloat />}
      {!isAdmin && <ChatWidget />}
    </div>
  );
};

export default function App() {
  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <BrowserRouter>
        <ContentProvider>
          <ScrollManager />
          <PresenceTracker />
          <Shell />
          <Toaster position="top-center" richColors closeButton />
        </ContentProvider>
      </BrowserRouter>
    </ReactLenis>
  );
}
